import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type EmbeddedProps = {
  /** like "/details/123?tab=info" (relative to the child's base) */
  subpath?: string;
  /** parent handler that updates the real browser URL */
  onNavigate: (to: string) => void;
};

export default function RouterSync({
  subpath = '/',
  onNavigate,
}: EmbeddedProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Simple state tracking to prevent loops
  const lastSubpathRef = useRef(subpath);
  const lastNotifiedPathRef = useRef<string | null>(null);
  const isNavigatingRef = useRef(false);

  const currentPath = `${location.pathname}${location.search}${location.hash}`;

  // Stable callback to prevent unnecessary re-renders
  const stableOnNavigate = useCallback(onNavigate, [onNavigate]);

  // Parent -> Child: Sync child router when parent changes subpath
  useEffect(() => {
    // Skip if already at the target path
    if (subpath === currentPath) {
      lastSubpathRef.current = subpath;
      return;
    }

    // Skip if we're currently navigating to prevent conflicts
    if (isNavigatingRef.current) {
      return;
    }

    // Only navigate if subpath actually changed
    if (subpath !== lastSubpathRef.current) {
      lastSubpathRef.current = subpath;
      lastNotifiedPathRef.current = subpath;
      isNavigatingRef.current = true;

      navigate(subpath, { replace: true });

      // Reset navigation flag quickly but after navigation is queued
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 10);
    }
  }, [subpath, currentPath, navigate]);

  // Child -> Parent: Notify parent when child navigates
  useEffect(() => {
    // Skip if we're in the middle of a parent-initiated navigation
    if (isNavigatingRef.current) {
      return;
    }

    // Only notify if path changed and is different from what parent expects
    if (
      currentPath !== subpath &&
      currentPath !== lastNotifiedPathRef.current
    ) {
      lastNotifiedPathRef.current = currentPath;
      stableOnNavigate(currentPath);
    }
  }, [currentPath, subpath, stableOnNavigate]);

  return null;
}
